import { useTranslation } from '@gimlite/common';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { forwardRef, useImperativeHandle, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePortalZIndex } from '../../hooks/usePortalZIndex';
import { ColorType } from '../../types/component.type';
import { Button, ButtonType } from '../button/button.component';
import { IconType } from '../icon/icon.component';
import { Status } from '../status/status.component';
import { Text } from '../text/text.component';
import './dialog.component.scss';

const variantMapper = {
  error: {
    foregroundColor: 'alert_error',
    backgroundColor: 'alert_error_20',
    defaultActionLabel: 'watermelon-delete',
    buttonVariant: 'alert_error',
    defaultIcon: 'delete',
  },
  warning: {
    foregroundColor: 'alert_warning',
    backgroundColor: 'alert_warning_20',
    defaultActionLabel: 'watermelon-validate',
    buttonVariant: 'alert_warning',
    defaultIcon: 'alarm',
  },
  basic: {
    foregroundColor: 'black_50',
    backgroundColor: 'black_4',
    defaultActionLabel: 'watermelon-validate',
    buttonVariant: 'primary',
    defaultIcon: 'globe',
  },
} as const satisfies Record<
  string,
  {
    foregroundColor: ColorType;
    backgroundColor: ColorType;
    defaultActionLabel: string;
    buttonVariant: ButtonType.Props['variant'];
    defaultIcon: IconType.Props['variant'];
  }
>;

export declare namespace DialogType {
  type Props = {
    className?: string | null;
    children?: React.ReactNode;
    customContent?: React.ReactNode;
    title: string;
    variant?: keyof typeof variantMapper;
    icon?: IconType.Props['variant'];
    description?: string | React.ReactNode | null;
    onCancel?: () => void;
  } & (
    | {
        mode: 'action';
        actionLabel?: string;
        cancelLabel?: string;
        onConfirm?: () => void;
      }
    | {
        mode: 'info';
      }
  );

  type Ref = {
    setIsOpen: (value: boolean) => void;
  };
}

const DialogComponent = forwardRef<DialogType.Ref, DialogType.Props>(
  (
    {
      children,
      className,
      title,
      customContent,
      description,
      onCancel,
      variant = 'error',
      icon,
      mode = 'action',
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const zIndex = usePortalZIndex(isOpen);

    useImperativeHandle(ref, () => ({
      setIsOpen: (value: boolean) => setIsOpen(value),
    }));

    return (
      <>
        <div
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(true);
          }}
        >
          {children}
        </div>
        {isOpen &&
          createPortal(
            <div
              className={classNames(`dialog`, className)}
              style={{ zIndex: zIndex ?? undefined }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="dialog__content">
                <div className="dialog__content__header">
                  {icon && (
                    <Status
                      icon={icon ?? variantMapper[variant].defaultIcon}
                      backgroundColor={variantMapper[variant].backgroundColor}
                      foregroundColor={variantMapper[variant].foregroundColor}
                    />
                  )}
                  <div className="dialog__content__header__present">
                    <Text
                      textAlign="center"
                      variant="headline"
                      whiteSpace="nowrap"
                    >
                      {title}
                    </Text>
                    {description &&
                      (typeof description === 'string' ? (
                        <Text textAlign="center" variant="body">
                          {description}
                        </Text>
                      ) : (
                        description
                      ))}
                    {customContent && (
                      <div className="dialog__content__header__present__custom">
                        {customContent}
                      </div>
                    )}
                  </div>
                </div>

                <div className="dialog__content__footer">
                  <Button
                    width={mode === 'action' ? 'max-content' : undefined}
                    minWidth={mode === 'info'}
                    onClick={() => {
                      onCancel?.();
                      setIsOpen(false);
                    }}
                    variant="secondary"
                    size="large"
                  >
                    {mode === 'action'
                      ? 'cancelLabel' in props
                        ? (props.cancelLabel ?? t('watermelon-cancel'))
                        : t('watermelon-cancel')
                      : t('watermelon-close')}
                  </Button>
                  {mode === 'action' && (
                    <Button
                      onClick={() => {
                        'onConfirm' in props ? props.onConfirm?.() : undefined;
                        setIsOpen(false);
                      }}
                      width="max-content"
                      variant={variantMapper[variant].buttonVariant}
                      size="large"
                    >
                      {'actionLabel' in props
                        ? (props.actionLabel ??
                          t(variantMapper[variant].defaultActionLabel))
                        : t(variantMapper[variant].defaultActionLabel)}
                    </Button>
                  )}
                </div>
              </div>
            </div>,
            document.body,
          )}
      </>
    );
  },
);

export const Dialog = observer(DialogComponent);
